import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Link,
  Paper,
  Snackbar,
} from '@mui/material';
import { Cookie } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  STORAGE_KEY,
  CookiePreferences,
  getStoredPreferences,
  COOKIE_PREFERENCES_CHANGED,
} from '../utils/cookieConsent';

export type { CookiePreferences } from '../utils/cookieConsent';
export { getStoredPreferences } from '../utils/cookieConsent';

const savePreferences = (prefs: CookiePreferences): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    window.dispatchEvent(
      new CustomEvent(COOKIE_PREFERENCES_CHANGED, { detail: prefs })
    );
  } catch (e) {
    console.warn('Failed to save cookie preferences', e);
  }
};

export const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string) => string };
  const [visible, setVisible] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    const stored = getStoredPreferences();
    setVisible(!stored);
  }, []);

  const handleAcceptAll = () => {
    const prefs: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      acknowledgedAt: new Date().toISOString(),
    };
    savePreferences(prefs);
    setVisible(false);
    setSnackbarOpen(true);
  };

  const handleEssentialOnly = () => {
    const prefs: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      acknowledgedAt: new Date().toISOString(),
    };
    savePreferences(prefs);
    setVisible(false);
    setSnackbarOpen(true);
  };

  if (!visible) return null;

  return (
    <>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          p: 2,
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
            <Cookie sx={{ color: 'primary.main', mt: 0.5, flexShrink: 0 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {t('cookieBanner.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cookieBanner.description')}{' '}
                <Link href="/privacy" color="primary" underline="hover">
                  {t('cookieBanner.privacyLink')}
                </Link>
              </Typography>
            </Box>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ flexShrink: 0 }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleEssentialOnly}
            >
              {t('cookieBanner.essentialOnly')}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleAcceptAll}
            >
              {t('cookieBanner.acceptAll')}
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={t('cookieBanner.saved')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80, sm: 24 } }}
      />
    </>
  );
};

export default CookieConsentBanner;
